import { uniq, property, chain, reject, contains, filter } from 'underscore';

import { getUtcTime } from '@bingads-webui-universal/primitive-utilities';

const getNewTextAssets = (assetGroup, originalAssetGroup) => {
  const headlines = [];
  const longHeadlines = [];
  const descriptions = [];
  let businessName;
  if (originalAssetGroup) {
    const { Headlines = {}, LongHeadlines = {}, Descriptions = {} } = assetGroup;
    const { Headlines: originalHeadlines = {}, LongHeadlines: originalLongHeadlines = {}, Descriptions: originalDescriptions = {} } = originalAssetGroup;
    const originalHeadlinesTexts = Object.values(originalHeadlines);
    const originalLongHeadlinesTexts = Object.values(originalLongHeadlines);
    const originalDescriptionsTexts = Object.values(originalDescriptions);
    Object.values(Headlines).forEach((headline) => {
      if (!originalHeadlinesTexts.includes(headline)) {
        headlines.push(headline);
      }
    });
    Object.values(LongHeadlines).forEach((longHeadline) => {
      if (!originalLongHeadlinesTexts.includes(longHeadline)) {
        longHeadlines.push(longHeadline);
      }
    });
    Object.values(Descriptions).forEach((description) => {
      if (!originalDescriptionsTexts.includes(description)) {
        descriptions.push(description);
      }
    });
    if (assetGroup.BusinessName !== originalAssetGroup.BusinessName) {
      businessName = assetGroup.BusinessName;
    }
  }
  return {
    headlines: uniq(headlines),
    longHeadlines: uniq(longHeadlines),
    descriptions: uniq(descriptions),
    businessName,
  };
};

const getNewImageAssetIds = (assetGroup, originalAssetGroup) => {
  const assetIds = chain(assetGroup)
    .result('Images', [])
    .result('ImageList', [])
    .flatten()
    .map(property('id'))
    .map(id => id.toString())
    .uniq()
    .value();

  const originalAssetIds = chain(originalAssetGroup)
    .result('Images', [])
    .result('ImageAssets', [])
    .map(property('Asset'))
    .map(property('Id'))
    .map(id => id.toString())
    .uniq()
    .value();

  return reject(assetIds, assetId => contains(originalAssetIds, assetId));
};

const getAppliedTextAssets = (allTextSuggestions, newTextAssets) => {
  const appliedTextAssets = [];
  appliedTextAssets.push(...allTextSuggestions.descriptions.filter(({ Text }) => newTextAssets.descriptions.includes(Text)));
  appliedTextAssets.push(...allTextSuggestions.headlines.filter(({ Text }) => newTextAssets.headlines.includes(Text)));
  appliedTextAssets.push(...allTextSuggestions.longHeadlines.filter(({ Text }) => newTextAssets.longHeadlines.includes(Text)));
  if (newTextAssets.businessName) {
    appliedTextAssets.push(...allTextSuggestions.businessName.filter(({ Text }) => newTextAssets.businessName === Text));
  }
  return appliedTextAssets
    .map(property('SuggestionId'))
    .filter(Boolean);
};

const getAppliedImageAssets = (selectedImageSuggestions, newImageAssetIds) => filter(selectedImageSuggestions, selectedImage => contains(newImageAssetIds, selectedImage.imageId.toString()));

const getRecommendationLog = ({
  isEditing,
  accountId,
  userId,
  campaignId,
  assetGroupId,
  finalUrl,
  isFinalUrlFromOfflineRecommendations,
  view,
  apply,
  returned,
  appliedImageAssetIds,
}) => {
  const logEntry = {
    Channel: 'bingadsweb',
    Feature: 'Pmax',
    Scenario: isEditing ? 'EditWorkFlow' : 'CreationWorkFlow',
    TimeStamp: getUtcTime(),
    AccountId: accountId,
    UserId: userId,
    FinalURL: finalUrl,
    UrlType: isFinalUrlFromOfflineRecommendations ? 'SuggestedURL' : 'NewURL',
    AcceptInfo: {
      ReturnedSuggestionIds: (returned && filter(returned, Boolean)) || [],
      ViewSuggestionIds: (view && filter(view, Boolean)) || [],
      ApplySuggestionIds: (apply && filter(apply, Boolean)) || [],
    },
    CampaignId: campaignId,
    AssetGroupId: assetGroupId,
    AppliedImgAssetIds: (appliedImageAssetIds && filter(appliedImageAssetIds, Boolean)) || [],
  };
  return logEntry;
};

export const logRecommendations = ({
  recommendationContext, activity,
  aid, uid, campaignId, isEditing,
  assetGroup, originalAssetGroup,
}) => {
  const { Id: assetGroupId } = assetGroup;
  if (activity && recommendationContext) {
    const newTextAssets = getNewTextAssets(assetGroup, originalAssetGroup);
    const newImageAssetIds = getNewImageAssetIds(assetGroup, originalAssetGroup);
    const { trackingData } = recommendationContext.tracking;
    const { allTextSuggestions, selectedImageSuggestions } = trackingData;
    const appliedTextAssets = getAppliedTextAssets(allTextSuggestions, newTextAssets);
    const appliedImageAssets = getAppliedImageAssets(selectedImageSuggestions, newImageAssetIds);

    const recommendationLog = getRecommendationLog({
      isEditing,
      accountId: aid,
      userId: uid,
      campaignId,
      assetGroupId,
      ...trackingData,
      apply: uniq([
        ...trackingData.apply || [],
        ...appliedTextAssets,
        ...appliedImageAssets.map(property('suggestionId')),
      ]),
      appliedImageAssetIds: appliedImageAssets.map(property('imageId')),
    });
    activity.signal(JSON.stringify(recommendationLog));
  }
};
